import React from "react";
import { Link, useLocation } from "react-router-dom";
import { navigationConfig } from "configs/NavigationConfig";
import IntlMessage from "../util-components/IntlMessage";
import "./PageHeader.css";

/******************************************************************
 * ##: nodeMatchesPath
 * Tells whether a navigation node owns the given pathname, matching
 * its own `path`, any exact `allowed_paths` entry, or an allowed
 * wildcard (`.*`) pattern.
 * @param {object} node - A navigation-config node
 * @param {string} pathname - The current location pathname
 * @returns {boolean} - True when the node represents this pathname
 * History:
 * 08-08-2026: Created
 ******************************************************************/
const nodeMatchesPath = (node, pathname) => {
  if (node.path === pathname) return true;
  if (Array.isArray(node.allowed_paths)) {
    return node.allowed_paths.some((ap) => {
      if (ap.path === pathname) return true;
      if (ap.path.includes(".*")) {
        try {
          return new RegExp(ap.path).test(pathname);
        } catch (e) {
          return false;
        }
      }
      return false;
    });
  }
  return false;
};

/******************************************************************
 * ##: findMenuChain
 * Walks the navigation tree depth-first and returns the chain of
 * nodes from the top-level group down to the node that owns the
 * pathname (or null when no node matches).
 * @param {Array} nodes - Navigation nodes at the current depth
 * @param {string} pathname - The current location pathname
 * @param {Array} chain - Accumulated ancestors (internal)
 * @returns {Array|null} - The matched ancestor chain, or null
 * History:
 * 08-08-2026: Created
 ******************************************************************/
const findMenuChain = (nodes, pathname, chain) => {
  for (const node of nodes) {
    const here = [...chain, node];
    if (nodeMatchesPath(node, pathname)) return here;
    if (Array.isArray(node.submenu) && node.submenu.length > 0) {
      const found = findMenuChain(node.submenu, pathname, here);
      if (found) return found;
    }
  }
  return null;
};

/******************************************************************
 * ##: PageHeader (global wayfinding bar)
 * Renders the per-page "you are here" chrome in the SalesPark Style
 * v3 language: a quiet trail of the parent MENU section(s) flowing
 * through chevrons into the current page as the bold title anchor.
 * The trail follows the navigation hierarchy (mirrors the sidebar),
 * not the URL path, so a section like "Vendas > Compradores" reads
 * correctly even when the page lives on an unrelated URL base.
 * @param {string} title - i18n id of the page title (defaults to "home")
 * @param {boolean} display - whether to render the header for this route
 * @param {Array} crumbs - optional explicit trail [{ label, to }] with plain
 *   labels, for routes outside navigationConfig (e.g. account); overrides the
 *   derived trail.
 * @param {string} plainTitle - optional plain-text title (not an i18n id),
 *   overrides `title` when set.
 * @returns {JSX.Element|null} - The page header, or null when hidden
 * History:
 * 08-08-2026: Created
 * 27-09-2026: Support explicit crumbs / plainTitle for non-nav routes
 ******************************************************************/
export const PageHeader = ({ title, display, crumbs, plainTitle }) => {
  const location = useLocation();
  if (!display) return null;

  let trail;
  if (Array.isArray(crumbs)) {
    // Explicit trail with plain labels (routes outside navigationConfig).
    trail = crumbs.map((c) => ({ title: c.label, path: c.to, plain: true }));
  } else {
    const chain = findMenuChain(navigationConfig(), location.pathname, []) || [];
    // Ancestors above the current page, minus the top-level app group
    // (first) and any container flagged not to appear in the breadcrumb.
    trail = chain
      .slice(1, -1)
      .filter((node) => node.breadcrumb !== false && node.title)
      .map((node) => ({ title: node.title, path: node.path, plain: false }));
  }

  return (
    <div className="pghd">
      {trail.length > 0 && (
        <nav className="pghd-trail" aria-label="Navegação estrutural">
          {trail.map((node, index) => (
            <React.Fragment key={node.path || index}>
              <Link className="pghd-crumb" to={node.path}>
                {node.plain ? node.title : <IntlMessage id={node.title} />}
              </Link>
              <span className="pghd-sep" aria-hidden="true">
                &#8250;
              </span>
            </React.Fragment>
          ))}
        </nav>
      )}
      <h3 className="pghd-title">{plainTitle ? plainTitle : <IntlMessage id={title ? title : "home"} />}</h3>
    </div>
  );
};

export default PageHeader;
